import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";
import ProfileCard from "shared/components/profileCard";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Icons } from "assets";
import Pagination from "shared/components/pagination";

import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useNavigate, useParams } from "react-router-dom";
import { routeConstant } from "shared/routes/routesConstant";
import {
  getPublisherDataService,
  getPublisherBooksService,
  allMouService,
} from "shared/services/adminService";
import { toastMessage } from "shared/components/toast";
import useWindowSize from "shared/customHooks/windowSize";
import SkeletonLoader from "./skeletonLoader";
import useDebounce from "shared/customHooks/useDebounce";
import { useSelector } from "react-redux";
import { rolesRoutes } from "shared/routes/allRoutes";
import {
  getPublisherBooksServiceProjectHead,
  getPublisherProfileService,
} from "shared/services/projectHeadService";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import ItemsPerPage from "shared/components/itemsPerPage";

function PublisherProfile() {
  const navigate = useNavigate();
  const { id }: any = useParams();
  const screenWidth = useWindowSize();
  const {
    user: { user },
  } = useSelector((state: any) => state.root);
  const [activePage, setActivePage] = useState<number>(1);
  const [profileUser, setProfileUser] = useState<any>(null);
  // The publisher detail endpoint does not return latest_mou, so the agreement
  // is fetched separately for the card at the top.
  const [latestMou, setLatestMou] = useState<any>(null);
  // Starts true: the profile is fetched on mount, so the first paint must be
  // the skeleton rather than an empty profile card.
  const [loader, setLoader] = useState<boolean>(true);
  const [allBooks, setAllBooks] = useState<any>(null);
  const [totalPage, setTotalPage] = useState<number>(0);
  // Starts true: the books are fetched on mount, so the first paint must be
  // the skeleton. Starting false flashes "No Data Found" before the request.
  const [bookLoader, setBookLoader] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [totalItems, setTotalItems] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const getProfileData = () => {
    let service =
      rolesRoutes?.find((role) => role.role_id === Number(user?.role))
        ?.role_id === 1
        ? getPublisherProfileService
        : getPublisherDataService;

    setLoader(true);
    service(id)
      .then(({ data }) => {
        setProfileUser(data?.data);
        setLoader(false);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("err", err?.response?.data?.message);
        setLoader(false);
      });
  };

  const getLatestMou = () => {
    allMouService(id)
      .then(({ data }: any) => {
        const mous = data?.data || [];
        // The list is not guaranteed to be ordered, so take the newest by id.
        const newest = mous.reduce(
          (acc: any, mou: any) =>
            !acc || Number(mou?.id) > Number(acc?.id) ? mou : acc,
          null
        );
        setLatestMou(newest);
      })
      .catch((err: any) => {
        // No agreement for this publisher is a normal state, not an error.
        console.log("err", err?.response?.data?.message);
      });
  };

  useEffect(() => {
    getProfileData();
    getLatestMou();
    // eslint-disable-next-line
  }, []);

  const getPublisherBooks = () => {
    let service =
      rolesRoutes?.find((role) => role.role_id === Number(user?.role))
        ?.role_id === 1
        ? getPublisherBooksServiceProjectHead
        : getPublisherBooksService;

    setBookLoader(true);
    service(id, search, activePage, itemsPerPage)
      .then(({ data }) => {
        setAllBooks(data?.data?.data);
        setTotalPage(data?.data?.last_page);
        setBookLoader(false);
        setTotalItems(data?.data?.total);
        setItemsPerPage(data?.data?.per_page);
        setCurrentPage(data?.data?.current_page);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("err", err?.response?.data?.message);
        setBookLoader(false);
      });
  };

  useEffect(() => {
    getPublisherBooks();
    // eslint-disable-next-line
  }, [searchText, activePage, itemsPerPage]);

  const BOOK_STATUS = [
    { id: 0, label: "Pending" },
    { id: 1, label: "Admin Review" },
    { id: 2, label: "Revision Requested" },
    { id: 3, label: "Revision Submitted" },
    { id: 4, label: "Approved" },
    { id: 5, label: "Published" },
    { id: 6, label: "Rejected" },
    { id: 7, label: "Internal Review" },
    { id: 8, label: "Review Completed" },
  ];

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800
  );

  useBreadcrumbs(
    {
      title: profileUser?.publisher?.full_name,
      path: routeConstant.publisherProfile.path.replace(":id", profileUser?.id),
    },
    true,
    profileUser
  );

  return (
    <div>
      <ProfileCard
        user={
          profileUser
            ? {
                ...profileUser,
                latest_mou: profileUser?.latest_mou || latestMou,
              }
            : profileUser
        }
        isPublisher
        isLoader={loader}
      />
      <Box className={styles.topLevelContainer}>
        <Box className={styles.topHeadContainer}>
          <Box>
            <div className={styles.tableHeading}>All books here</div>
            <div className={styles.tableSubHeading}>
              Manage user details from here.
            </div>
          </Box>
          <Box className={styles.filterContainer}>
            <div className={styles.gridContainer}>
              <Icons.SearchIcon className={styles.searchIcon} />
              <input
                placeholder="Search"
                className={styles.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </Box>
        </Box>
        <TableContainer
          component={Paper}
          className={styles.table}
          sx={screenWidth > 990 ? { width: `${screenWidth - 300}px` } : {}}
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow className={styles.tableHead}>
                <TableCell>Book name</TableCell>
                <TableCell>Author name</TableCell>
                <TableCell>Genre</TableCell>
                <TableCell>Age range</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookLoader ? (
                <SkeletonLoader />
              ) : (
                allBooks?.map((item: any) => {
                  return (
                    <>
                      <TableRow className={styles.tableRow}>
                        <TableCell>
                          <Box display={"flex"} alignItems={"center"}>
                            <img
                              src={item?.thumbnail}
                              alt="book cover"
                              className={styles.bookCover}
                            />
                            <span style={{ marginLeft: "10px" }}>
                              {item?.title}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell>{item?.book_author?.name}</TableCell>
                        <TableCell>{item?.genres[0]?.name}</TableCell>
                        <TableCell>{item?.age_range?.text}</TableCell>
                        <TableCell>
                          {item?.price ? item?.price : "Not provided"}
                        </TableCell>
                        <TableCell>
                          <span
                            className={
                              item?.status === 4 || item?.status === 5
                                ? styles.statusApproved
                                : styles.statusRejected
                            }
                          >
                            {
                              BOOK_STATUS.find(
                                (itm: any) => itm?.id === Number(item?.status)
                              )?.label
                            }
                          </span>
                        </TableCell>
                        <TableCell>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            className={styles.actionBtn}
                          >
                            <button
                              onClick={() =>
                                navigate(
                                  routeConstant.bookDetail.path.replace(
                                    ":id",
                                    item?.id
                                  )
                                )
                              }
                            >
                              <RemoveRedEyeIcon />
                            </button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!(allBooks?.length > 0) && !bookLoader && (
          <div className={styles.noUserCard}>
            <Icons.NoDataIcon />
            <h5>No Data Found</h5>
          </div>
        )}

        {totalPage > 1 && (
          <Pagination
            totalPages={totalPage}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        )}
        {allBooks?.length > 0 ? (
          <ItemsPerPage
            setActivePage={setActivePage}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          ></ItemsPerPage>
        ) : null}
      </Box>
    </div>
  );
}

export default PublisherProfile;
